import { useRef, useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Send, Bot, Loader2 } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { API, fmtErr } from "@/lib/api";

const SUGGESTIONS = [
  "Ringkas progres produksi bulan ini",
  "Vendor mana yang paling tertinggal?",
  "Prediksi kapan target 2.775 ton tercapai",
];

export default function AICopilot({ open, setOpen }) {
  const { user } = useApp();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId] = useState(() => {
    let s = localStorage.getItem("hl_sid");
    if (!s) { s = crypto.randomUUID(); localStorage.setItem("hl_sid", s); }
    return s;
  });
  const scroller = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      fetch(`${API}/ai/history?session_id=${sessionId}`, { credentials: "include" })
        .then((r) => r.json())
        .then((d) => setMessages(Array.isArray(d) ? d.map((m) => ({ role: m.role, content: m.content })) : []))
        .catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const send = async (text) => {
    if (!text.trim() || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (res.status === 401) throw new Error("Sesi berakhir, muat ulang halaman");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const line of parts) {
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (payload === "[DONE]") continue;
          try {
            const j = JSON.parse(payload);
            if (j.error) {
              setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + "⚠️ " + j.error }; return c; });
            } else if (j.delta) {
              const d = j.delta;
              setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + d }; return c; });
            }
          } catch (e) { /* skip malformed chunk */ }
        }
      }
    } catch (e) {
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: "⚠️ " + (e.message || "Gagal menghubungi AI") }; return c; });
    }
    setBusy(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" onClick={() => setOpen(false)} data-testid="ai-copilot-backdrop" />
          <motion.aside
            initial={{ x: 440 }} animate={{ x: 0 }} exit={{ x: 440 }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 z-50 flex h-full w-full max-w-[420px] flex-col border-l border-slate-800 bg-[#0B111C]"
            data-testid="ai-copilot-panel"
          >
            <div className="flex items-center justify-between border-b border-slate-800 px-4 py-3">
              <div className="flex items-center gap-2.5">
                <div className="flex h-9 w-9 items-center justify-center rounded bg-sky-500/15 border border-sky-500/30">
                  <Bot className="h-4.5 w-4.5 h-[18px] w-[18px] text-sky-400" />
                </div>
                <div>
                  <div className="font-display text-sm font-bold text-slate-100">HL-Copilot</div>
                  <div className="cn text-[10px] text-slate-500">AI 生产助手 · {user?.name?.split(" ")[0]}</div>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="rounded p-1.5 text-slate-400 hover:bg-slate-800 hover:text-slate-200 transition-colors" data-testid="ai-copilot-close-button">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div ref={scroller} className="flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="ai-copilot-messages">
              {messages.length === 0 && (
                <div className="pt-6 text-center">
                  <Bot className="mx-auto h-10 w-10 text-slate-700" />
                  <p className="mt-3 text-sm text-slate-400">Tanya apa saja tentang produksi, QC, STB, dan delivery.</p>
                  <p className="cn text-xs text-slate-600">询问有关生产、质检、交付的任何问题</p>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                  <div className={
                    m.role === "user"
                      ? "max-w-[85%] rounded-lg rounded-br-sm bg-sky-600/80 px-3 py-2 text-[13px] text-white whitespace-pre-wrap"
                      : "max-w-[90%] whitespace-pre-wrap rounded-lg rounded-bl-sm border border-slate-800 bg-[#101a2c] px-3 py-2 text-[13px] text-slate-200"
                  } data-testid={m.role === "assistant" ? `ai-message-${i}` : `user-message-${i}`}>
                    {m.content || (busy && i === messages.length - 1 ? <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-500" /> : "")}
                  </div>
                </div>
              ))}
            </div>

            {messages.length === 0 && (
              <div className="flex flex-wrap gap-2 px-4 pb-2">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => send(s)} className="rounded-full border border-slate-700 px-3 py-1.5 text-[11px] text-slate-300 hover:border-sky-500/50 hover:text-sky-300 transition-colors" data-testid="ai-suggestion-chip">
                    {s}
                  </button>
                ))}
              </div>
            )}

            <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="flex gap-2 border-t border-slate-800 p-3">
              <input
                value={input} onChange={(e) => setInput(e.target.value)}
                placeholder="Ketik pertanyaan… / 输入问题"
                className="flex-1 rounded bg-slate-900 border border-slate-700 px-3 py-2 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-sky-500/60"
                data-testid="ai-copilot-input"
              />
              <button type="submit" disabled={busy || !input.trim()}
                className="rounded bg-sky-600 px-3 text-white hover:bg-sky-500 disabled:opacity-40 transition-colors" data-testid="ai-copilot-send-button">
                <Send className="h-4 w-4" />
              </button>
            </form>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}